import {
	textBlock,
	TextBlockProps,
	textStyle,
	TextStyleProps,
} from '@/uiPrimitives/typography/text';
import { getHyphensPropValue } from './hyphens';
import { JSX, ReactNode } from 'react';
import { classes } from '@/uiPrimitives/base/classes.helpers';

const headingBaseStyle = {
	fontWeight: 'bold',
	capLineHeight: 'tight',
	fontVariantNumeric: 'lining-nums',
	textWrap: 'balance',
} as const satisfies TextStyleProps;
type HeadingSizes = 'h1' | 'h2' | 'h3' | 'h4';

const capSizes = {
	h1: '3 | h1',
	h2: '2 | h2',
	h3: '1 | h3',
	h4: '0 | p',
} as const satisfies Record<HeadingSizes, TextStyleProps['capSize']>;

const hyphensThreshold = {
	h1: 15,
	h2: 20,
	h3: undefined,
	h4: undefined,
} as const satisfies Record<HeadingSizes, number | undefined>;

const getHeadingStyle = ({
	children: children,
	size,
}: {
	children?: ReactNode;
	size: HeadingSizes;
}) => {
	const hyphens =
		typeof children === 'string' && hyphensThreshold[size] !== undefined
			? getHyphensPropValue({
					text: children,
					threshold: hyphensThreshold[size],
				})
			: undefined;

	return {
		...headingBaseStyle,
		capSize: capSizes[size],
		hyphens,
	} as const satisfies TextStyleProps;
};

export const h1TextStyle = (props: TextStyleProps) =>
	textStyle({
		...getHeadingStyle({ size: 'h1' }),
		...props,
	});

export const h1TextBlock = ({
	text,
	...props
}: Omit<TextBlockProps, 'capSize'> & { text: string | undefined }) =>
	textBlock({
		...getHeadingStyle({
			children: text,
			size: 'h1',
		}),
		...props,
	});

export const H1TextBlock = ({
	className,
	children,
	textBlockProps,
	size = 'h1',
	...props
}: {
	children?: string;
	size?: HeadingSizes;
	textBlockProps?: Omit<TextBlockProps, 'capSize'>;
} & Omit<JSX.IntrinsicElements['h1'], 'children'>) => (
	<h1
		{...props}
		className={classes(
			className,
			textBlock({
				...getHeadingStyle({
					children,
					size,
				}),
				...textBlockProps,
			}),
		)}
	>
		{children}
	</h1>
);

export const h2TextStyle = ({ ...props }: TextStyleProps) =>
	textStyle({
		...getHeadingStyle({
			size: 'h2',
		}),
		...props,
	});
export const h2TextBlock = ({
	text,
	...props
}: Omit<TextBlockProps, 'capSize'> & { text: string | undefined }) =>
	textBlock({
		...getHeadingStyle({
			size: 'h2',
			children: text,
		}),
		...props,
	});

export const H2TextBlock = ({
	className,
	children,
	textBlockProps,
	size = 'h2',
	...props
}: {
	children?: string;
	size?: HeadingSizes;
	textBlockProps?: Omit<TextBlockProps, 'capSize'>;
} & Omit<JSX.IntrinsicElements['h2'], 'children'>) => (
	<h2
		{...props}
		className={classes(
			className,
			textBlock({
				...getHeadingStyle({
					children,
					size,
				}),
				...textBlockProps,
			}),
		)}
	>
		{children}
	</h2>
);

export const h3TextStyle = (props: TextStyleProps) =>
	textStyle({
		...getHeadingStyle({ size: 'h3' }),
		...props,
	});
export const h3TextBlock = (props: Omit<TextBlockProps, 'capSize'>) =>
	textBlock({
		...getHeadingStyle({ size: 'h3' }),
		...props,
	});

export const H3TextBlock = ({
	className,
	children,
	textBlockProps = {},
	size = 'h3',
	...props
}: {
	textBlockProps?: TextBlockProps;
	size?: HeadingSizes;
} & JSX.IntrinsicElements['h3']) => (
	<h3
		{...props}
		className={classes(
			className,
			textBlock({
				...getHeadingStyle({
					children,
					size,
				}),
				...textBlockProps,
			}),
		)}
	>
		{children}
	</h3>
);

export const h4TextStyle = (props: TextStyleProps) =>
	textStyle({
		...getHeadingStyle({ size: 'h4' }),
		...props,
	});
export const h4TextBlock = (props: Omit<TextBlockProps, 'capSize'>) =>
	textBlock({
		...getHeadingStyle({ size: 'h4' }),
		...props,
	});

export const H4TextBlock = ({
	className,
	children,
	textBlockProps = {},
	size = 'h4',
	...props
}: {
	textBlockProps?: Omit<TextBlockProps, 'capSize'>;
	size?: HeadingSizes;
} & JSX.IntrinsicElements['h4']) => (
	<h4
		{...props}
		className={classes(
			className,
			textBlock({
				...getHeadingStyle({
					children,
					size,
				}),
				...textBlockProps,
			}),
		)}
	>
		{children}
	</h4>
);
